import type { Asset, ValueChainImportanceRating, ValueChainStage } from "@/lib/assets";
import { hazardKeysFor, hazardTypeOfKey, type AssetHazardResult, type HazardKey } from "@/lib/results";
import { getResultsForHorizon } from "@/lib/results-filters";
import { percentAtHighRiskPerHazard } from "@/lib/takeaways";
import { PRECIPITATION_DRY_SITE_NOTE } from "@/lib/report-basis";
import { HAZARD_TYPES, type ClimateScenario, type HazardType, type TimeHorizon } from "@shared/scoring/types";

/**
 * Pure stat functions for the client dashboard (design spec
 * docs/superpowers/specs/2026-09-27-client-dashboard-design.md).
 *
 * No fetching, no React, no formatting — callers format. Everything
 * risk-related delegates to client/src/lib/takeaways.ts rather than
 * recomputing it, so "% at high risk" has exactly one implementation in this
 * codebase and the dashboard can never disagree with Tab 2 or the Tab 4
 * report.
 */

export interface PortfolioStats {
  assetCount: number;
  countryCount: number;
  /** Business importance 4. Labelled "Very high" per LEVEL_LABELS[4]. */
  veryHighImportanceCount: number;
  /** Distinct stages that have at least one asset. */
  stagesCovered: number;
  /** Every stage available to this workspace — the API is already sector-scoped. */
  stagesTotal: number;
}

export function computePortfolioStats(assets: Asset[], stages: ValueChainStage[]): PortfolioStats {
  const countries = new Set(assets.map((a) => a.country.trim()).filter((c) => c.length > 0));
  const coveredStageIds = new Set(
    assets.map((a) => a.valueChainStageId).filter((id): id is string => id !== null),
  );

  return {
    assetCount: assets.length,
    countryCount: countries.size,
    veryHighImportanceCount: assets.filter((a) => a.businessImportance === 4).length,
    stagesCovered: coveredStageIds.size,
    stagesTotal: stages.length,
  };
}

export interface ReadinessStats {
  assetsMissingCoordinates: number;
  assetsMissingStage: number;
  /**
   * Stages with NO rating row at all. A rating of `0` is NOT counted: it means
   * "Not applicable" (ValueChainImportanceForm.tsx's RATING_LEGEND), a
   * deliberate answer, and counting it would produce a permanently amber
   * readiness row no client action could ever clear.
   *
   * Because the form saves every stage at once, after the first save this is
   * effectively "has the client been through the value chain step?" — it
   * cannot distinguish "never answered" from "answered Not applicable". That
   * is the intended readiness signal; the schema supports nothing finer.
   */
  stagesUnrated: number;
  stagesTotal: number;
  /** Null until the slow fetch group resolves. */
  coverage: CoverageStats | null;
}

export function computeReadinessStats(
  assets: Asset[],
  stages: ValueChainStage[],
  ratings: ValueChainImportanceRating[],
  results: AssetHazardResult[] | null,
): ReadinessStats {
  const ratedStageIds = new Set(ratings.map((r) => r.valueChainStageId));

  return {
    assetsMissingCoordinates: assets.filter(missingCoordinates).length,
    assetsMissingStage: assets.filter((a) => a.valueChainStageId === null).length,
    stagesUnrated: stages.filter((s) => !ratedStageIds.has(s.id)).length,
    stagesTotal: stages.length,
    coverage: results === null ? null : computeCoverage(assets, results),
  };
}

/** `latitude`/`longitude` are `string | null` on the wire — "0" is valid. */
function missingCoordinates(asset: Asset): boolean {
  return asset.latitude === null || asset.longitude === null;
}

export interface CoverageStats {
  /**
   * Current-horizon unresolved rows the analyst can fix by adding
   * coordinates. Excludes `changing_precipitation`, which coordinates never
   * unblock.
   */
  blockedByMissingSiteData: number;
  /** Current-horizon unresolved rows outside the workspace's control. */
  awaitingPlatformData: number;
  /** The distinct hazards behind `awaitingPlatformData`, in HAZARD_TYPES order. */
  awaitingPlatformDataHazards: HazardType[];
  /**
   * Current-horizon precipitation rows deliberately left unscored by the
   * dry-site rule (seasonal baseline < 50 mm). A methodology decision, not a
   * gap, so it sits in neither bucket above.
   */
  notScoredDrySite: number;
}

/** A definitive answer, in neither gap bucket. `not_applicable` is landslide
 * LHASA class 0-2: no risk, not unscored. */
const RESOLVED_STATUSES = new Set<AssetHazardResult["status"]>(["computed", "not_applicable"]);

/**
 * Two buckets with different owners, never one percentage: the analyst can
 * fix only the first (by adding coordinates).
 *
 * Current horizon only. `getResultsForHorizon` ignores `scenario` for
 * "historical", so the scenario passed here is immaterial — which is why
 * `computeReadinessStats` takes no scenario argument and §3's toggle cannot
 * move these numbers.
 *
 * A row whose assetId isn't in `assets` falls into awaitingPlatformData. Not
 * reachable through /api/results (it generates rows from the workspace's own
 * assets), so it's untested; the fallback is deliberate rather than an
 * oversight — an unknown asset is definitionally not one the analyst can fix
 * by adding coordinates.
 */
function computeCoverage(assets: Asset[], results: AssetHazardResult[]): CoverageStats {
  const unsitedAssetIds = new Set(assets.filter(missingCoordinates).map((a) => a.id));
  const currentRows = getResultsForHorizon(results, "historical", "SSP2-4.5");

  let blockedByMissingSiteData = 0;
  let awaitingPlatformData = 0;
  let notScoredDrySite = 0;
  const awaitingHazards = new Set<HazardType>();

  for (const resultRow of currentRows) {
    if (RESOLVED_STATUSES.has(resultRow.status)) continue;

    if (resultRow.status === "no_data" && resultRow.statusNote === PRECIPITATION_DRY_SITE_NOTE) {
      notScoredDrySite += 1;
      continue;
    }

    if (unsitedAssetIds.has(resultRow.assetId)) {
      blockedByMissingSiteData += 1;
      continue;
    }

    awaitingPlatformData += 1;
    awaitingHazards.add(resultRow.hazardType);
  }

  return {
    blockedByMissingSiteData,
    awaitingPlatformData,
    awaitingPlatformDataHazards: HAZARD_TYPES.filter((h) => awaitingHazards.has(h)),
    notScoredDrySite,
  };
}

/**
 * Hazards that actually carry a projection at this horizon/scenario, in
 * HAZARD_TYPES order.
 *
 * NOT row presence: results.routes.ts emits a row for every asset x hazard x
 * horizon x scenario whether or not the DB has one (pickHazardRow returns
 * `status: undefined` on no match and the route still pushes a "pending" row
 * with hazardScore unset). Counting rows would claim all 6 hazards have 2050
 * projections when only 3 do.
 *
 * The `not_applicable` clause is load-bearing, not defensive: a landslide row
 * at LHASA class 0-2 has `status: "not_applicable"` and NO hazardScore
 * (results.routes.ts:340-341). Testing hazardScore alone would drop landslide
 * — one of only 3 hazards with future data, and the only one producing N/A —
 * from the 2050 column entirely, reporting a definitive answer as an absence.
 */
export function hazardsWithScoresForHorizon(
  results: AssetHazardResult[],
  horizon: TimeHorizon,
  scenario: ClimateScenario,
): HazardType[] {
  const withData = new Set<HazardType>();

  for (const resultRow of getResultsForHorizon(results, horizon, scenario)) {
    if (resultRow.hazardScore !== undefined || resultRow.status === "not_applicable") {
      withData.add(resultRow.hazardType);
    }
  }

  return HAZARD_TYPES.filter((h) => withData.has(h));
}

/** One summary line (HazardKey): precipitation's seasons are separate deltas. */
export interface HorizonDelta {
  key: HazardKey;
  hazardType: HazardType;
  currentPct: number;
  futurePct: number;
  /** futurePct - currentPct. Negative when a hazard improves. */
  deltaPct: number;
}

/**
 * Change in high-risk share from Current to 2050, for hazards with a
 * non-null percentage on BOTH legs — a hazard missing from 2050 is excluded
 * rather than reported as a zero or null delta, which would read as "no
 * change" when the truth is "no data".
 *
 * Percentages come from percentAtHighRiskPerHazard, never recomputed here.
 */
export function computeHorizonDeltas(
  results: AssetHazardResult[],
  scenario: ClimateScenario,
): HorizonDelta[] {
  const currentPctByHazard = highRiskPctByHazard(results, "historical", scenario);
  const futurePctByHazard = highRiskPctByHazard(results, "2050", scenario);

  const deltas: HorizonDelta[] = [];
  for (const key of hazardKeysFor(HAZARD_TYPES, results)) {
    const currentPct = currentPctByHazard.get(key);
    const futurePct = futurePctByHazard.get(key);
    if (currentPct === undefined || futurePct === undefined) continue;
    deltas.push({ key, hazardType: hazardTypeOfKey(key), currentPct, futurePct, deltaPct: futurePct - currentPct });
  }

  return deltas.sort((a, b) => b.deltaPct - a.deltaPct);
}

/** Omits hazards whose pct is null (nothing computed to divide by). */
function highRiskPctByHazard(
  results: AssetHazardResult[],
  horizon: TimeHorizon,
  scenario: ClimateScenario,
): Map<HazardKey, number> {
  const rows = getResultsForHorizon(results, horizon, scenario);
  const pctByHazard = new Map<HazardKey, number>();

  for (const entry of percentAtHighRiskPerHazard(rows)) {
    if (entry.pct !== null) pctByHazard.set(entry.key, entry.pct);
  }

  return pctByHazard;
}

export function assetsByCountry(assets: Asset[]): Array<{ country: string; count: number }> {
  const countByCountry = new Map<string, number>();

  for (const asset of assets) {
    const country = asset.country.trim();
    if (country.length === 0) continue;
    countByCountry.set(country, (countByCountry.get(country) ?? 0) + 1);
  }

  // Array.from, not [...map.entries()] — this project's tsconfig sets no
  // `target`, so spreading a Map iterator trips TS2802. Same reason
  // takeaways.ts:63 uses Array.from.
  return Array.from(countByCountry.entries())
    .map(([country, count]) => ({ country, count }))
    .sort((a, b) => b.count - a.count || a.country.localeCompare(b.country));
}

/**
 * Value-chain order, not count order — the bars read as a sequence, and the
 * order stays stable as counts change.
 *
 * Assets with no stage are omitted rather than bucketed as "Unassigned": §2
 * already reports that count as an actionable gap, and a bar would invite
 * reading it as a real stage.
 */
export function assetsByStage(
  assets: Asset[],
  stages: ValueChainStage[],
): Array<{ stageId: string; stageName: string; count: number }> {
  const countByStageId = new Map<string, number>();

  for (const asset of assets) {
    if (asset.valueChainStageId === null) continue;
    countByStageId.set(asset.valueChainStageId, (countByStageId.get(asset.valueChainStageId) ?? 0) + 1);
  }

  return stages
    .filter((stage) => countByStageId.has(stage.id))
    .map((stage) => ({
      stageId: stage.id,
      stageName: stage.name,
      count: countByStageId.get(stage.id) ?? 0,
    }));
}
